import { useState, useEffect, useRef, useCallback } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Home, MessageSquare, Users, User, Search, Bell, X, Hash, Mail, ThumbsUp } from 'lucide-react';
import HomePage from './HomePage';
import CommunitiesPage from './CommunitiesPage';
import CommunityPage from './CommunityPage';
import MessagesPage from './MessagesPage';
import ProfilePage from './ProfilePage';
import HelpPage from './HelpPage';
import SuggestionsPage from './SuggestionsPage';
import { useAuth, API, getAuthHeaders } from '@/App';
import { AVATAR_OPTIONS, EMOJI_AVATARS } from './ProfilePage';

// Notification Dropdown Component with detailed notifications
function NotificationDropdown({ isOpen, onClose, notifications = [], onClearAll, onClickNotification, navigate }) {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const newNotifications = notifications.filter(n => !n.read);
  const oldNotifications = notifications.filter(n => n.read);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'dm':
        return <Mail className="w-5 h-5 text-purple-600" />;
      case 'reply':
      case 'nested_reply':
        return <MessageSquare className="w-5 h-5 text-indigo-600" />;
      case 'like':
        return <ThumbsUp className="w-5 h-5 text-pink-600" />;
      case 'post':
        return <Hash className="w-5 h-5 text-green-600" />;
      default:
        return <Bell className="w-5 h-5 text-gray-600" />;
    }
  };

  const getNotificationText = (notif) => {
    switch (notif.type) {
      case 'dm':
        return `sent you a message`;
      case 'reply':
        return `replied to your post`;
      case 'nested_reply':
        return `replied to your comment`;
      case 'like':
        return `liked your post`;
      case 'post':
        return `posted in`;
      default:
        return `interacted with your content`;
    }
  };

  const handleNotificationClick = (notif) => {
    onClickNotification(notif);
    onClose();
    
    if (notif.type === 'dm') {
      navigate(`/messages?user=${notif.from_user_id}&name=${encodeURIComponent(notif.from_user_name)}`);
    } else if (notif.community_id) {
      navigate(`/community/${notif.community_id}`);
    }
  };

  const NotificationItem = ({ notif }) => (
    <button
      key={notif.notification_id}
      onClick={() => handleNotificationClick(notif)}
      className={`w-full p-3 hover:bg-gray-50 dark:hover:bg-[#1a1a1a] flex items-start gap-3 border-b border-gray-100 dark:border-[#2a2a2a] last:border-b-0 text-left ${
        !notif.read ? 'bg-indigo-50/50 dark:bg-purple-500/10' : ''
      }`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
        notif.type === 'dm' ? 'bg-purple-100 dark:bg-purple-500/20' : 
        notif.type === 'like' ? 'bg-pink-100 dark:bg-pink-500/20' :
        notif.type === 'post' ? 'bg-green-100 dark:bg-green-500/20' :
        'bg-indigo-100 dark:bg-purple-500/20'
      }`}>
        {getNotificationIcon(notif.type)}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-900 dark:text-white">
          <span className="font-semibold">{notif.from_user_name}</span>{' '}
          {getNotificationText(notif)}
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">{notif.content_preview}</p>
        {notif.community_id && (
          <p className="text-xs text-indigo-600 dark:text-purple-400 mt-0.5">in {notif.community_id.toUpperCase()}</p>
        )}
      </div>
      {!notif.read && <div className="w-2 h-2 bg-purple-500 rounded-full shrink-0 mt-1"></div>}
    </button>
  );

  return (
    <div 
      ref={dropdownRef}
      className="absolute top-full right-0 mt-2 w-80 bg-white dark:bg-[#141414] rounded-xl shadow-xl border border-gray-200 dark:border-[#2a2a2a] overflow-hidden z-[60]"
    >
      <div className="p-4 border-b border-gray-100 dark:border-[#2a2a2a] flex items-center justify-between">
        <h3 className="font-semibold text-gray-900 dark:text-white">Notifications</h3>
        <div className="flex items-center gap-2">
          {notifications.length > 0 && (
            <button 
              onClick={onClearAll}
              className="text-xs text-indigo-600 dark:text-purple-400 hover:text-indigo-700 dark:hover:text-purple-300 font-medium"
            >
              Clear All
            </button>
          )}
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
      
      {notifications.length > 0 ? (
        <div className="max-h-96 overflow-y-auto">
          {/* New Notifications Section */}
          {newNotifications.length > 0 && (
            <>
              <div className="px-3 py-2 bg-purple-50 dark:bg-purple-500/10 border-b border-gray-100 dark:border-[#2a2a2a]">
                <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 uppercase tracking-wide">
                  New ({newNotifications.length})
                </span>
              </div>
              {newNotifications.slice(0, 10).map((notif) => (
                <NotificationItem key={notif.notification_id} notif={notif} />
              ))}
            </>
          )}
          
          {/* Old Notifications Section */}
          {oldNotifications.length > 0 && (
            <>
              <div className="px-3 py-2 bg-gray-50 dark:bg-[#1a1a1a] border-b border-gray-100 dark:border-[#2a2a2a]">
                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                  Earlier ({oldNotifications.length})
                </span>
              </div>
              {oldNotifications.slice(0, 10).map((notif) => (
                <NotificationItem key={notif.notification_id} notif={notif} />
              ))}
            </>
          )}
        </div>
      ) : (
        <div className="p-8 text-center">
          <Bell className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No notifications</p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">We'll notify you when something arrives</p>
        </div>
      )}
    </div>
  );
}

// Search Dropdown Component
function SearchDropdown({ isOpen, searchQuery, results, onClose, navigate }) {
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  if (!isOpen || !searchQuery.trim()) return null;

  return (
    <div 
      ref={dropdownRef}
      className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-[#141414] rounded-xl shadow-xl border border-gray-200 dark:border-[#2a2a2a] overflow-hidden z-[60]"
    >
      {results.length > 0 ? (
        <div className="max-h-64 overflow-y-auto">
          <div className="p-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-[#1a1a1a] border-b border-gray-100 dark:border-[#2a2a2a]">
            Courses
          </div>
          {results.map((community) => (
            <button
              key={community.community_id}
              onClick={() => {
                navigate(`/community/${community.community_id}`);
                onClose();
              }}
              className="w-full p-3 hover:bg-gray-50 dark:hover:bg-[#1a1a1a] flex items-center gap-3 border-b border-gray-100 dark:border-[#2a2a2a] last:border-b-0"
            >
              <div className="w-10 h-10 bg-indigo-100 dark:bg-purple-500/20 rounded-lg flex items-center justify-center">
                <Hash className="w-5 h-5 text-indigo-600 dark:text-purple-400" />
              </div>
              <div className="flex-1 text-left">
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {community.name}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  {community.description}
                </p>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="p-6 text-center">
          <Search className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No courses found for "{searchQuery}"</p>
          <button 
            onClick={() => {
              navigate('/suggestions');
              onClose();
            }}
            className="text-xs text-indigo-600 hover:text-indigo-700 mt-2 font-medium"
          >
            Request this course
          </button>
        </div>
      )}
    </div>
  );
}

export default function MainApp() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, checkAuth } = useAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [unreadCommunities, setUnreadCommunities] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [communities, setCommunities] = useState([]);

  // Fetch communities for search
  useEffect(() => {
    const fetchCommunities = async () => {
      try {
        const response = await fetch(`${API}/communities`, {
          headers: { ...getAuthHeaders() }
        });
        if (response.ok) {
          const data = await response.json();
          setCommunities(data);
        }
      } catch (error) {
        console.error('Failed to fetch communities:', error);
      }
    };
    fetchCommunities();
  }, []);

  // Filter communities based on search
  useEffect(() => {
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const filtered = communities.filter(c => 
        c.name.toLowerCase().includes(query) || 
        c.description?.toLowerCase().includes(query) ||
        c.community_id.toLowerCase().includes(query)
      );
      setSearchResults(filtered);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  }, [searchQuery, communities]);

  // Fetch notifications (only for logged in users)
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    try {
      // Fetch notification count
      const countResponse = await fetch(`${API}/notifications/count`, {
        headers: { ...getAuthHeaders() }
      });
      if (countResponse.ok) {
        const countData = await countResponse.json();
        setNotificationCount(countData.count || 0);
      }
      
      // Fetch notifications list
      const notifsResponse = await fetch(`${API}/notifications`, {
        headers: { ...getAuthHeaders() }
      });
      if (notifsResponse.ok) {
        const notifsData = await notifsResponse.json();
        setNotifications(notifsData || []);
      }
      
      // Fetch unread communities for red badges
      const unreadResponse = await fetch(`${API}/communities/unread`, {
        headers: { ...getAuthHeaders() }
      });
      if (unreadResponse.ok) {
        const unreadData = await unreadResponse.json();
        setUnreadCommunities(unreadData || {});
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, [user]);

  useEffect(() => {
    fetchNotifications();
    // Poll every 10 seconds for real-time notifications
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Clear all notifications
  const handleClearAll = async () => {
    try {
      const response = await fetch(`${API}/notifications/clear-all`, {
        method: 'POST',
        headers: { ...getAuthHeaders() }
      });
      if (response.ok) {
        setNotifications([]);  // Clear all notifications from state
        setNotificationCount(0);
      }
    } catch (error) {
      console.error('Failed to clear notifications:', error);
    }
  };

  // Mark single notification as read
  const handleClickNotification = async (notif) => {
    if (notif.read) return;
    try {
      await fetch(`${API}/notifications/${notif.notification_id}/read`, {
        method: 'POST',
        headers: { ...getAuthHeaders() }
      });
      setNotifications(prev => prev.map(n => 
        n.notification_id === notif.notification_id ? { ...n, read: true } : n
      ));
      setNotificationCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification as read:', error);
    }
  };

  // Check if profile avatar is an emoji
  const isEmojiAvatar = user?.profile_avatar && EMOJI_AVATARS?.includes(user.profile_avatar);

  const navItems = [
    { path: '/home', icon: Home, label: 'Home' },
    { path: '/communities', icon: Users, label: 'Communities' },
    { path: '/messages', icon: MessageSquare, label: 'Messages' },
    { path: '/profile', icon: User, label: 'Profile' }
  ];

  const isActive = (path) => {
    // Exact match for each route
    if (path === '/home') {
      return location.pathname === '/home' || location.pathname === '/';
    }
    if (path === '/communities') {
      return location.pathname === '/communities';
    }
    if (path === '/messages') {
      return location.pathname === '/messages';
    }
    if (path === '/profile') {
      return location.pathname === '/profile';
    }
    if (path === '/settings') {
      return location.pathname === '/settings';
    }
    return false;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-50 to-gray-100 dark:from-[#0a0a0a] dark:via-[#0a0a0a] dark:to-[#0f0f0f] p-2 md:p-4">
      {/* Top Navbar - Floating */}
      <nav className="bg-white dark:bg-[#0f0f0f] dark:border dark:border-[#1a1a1a] rounded-2xl shadow-lg shadow-gray-200/50 dark:shadow-none mb-4 sticky top-2 md:top-4 z-50">
        <div className="px-3 md:px-6 py-3 md:py-4 flex items-center justify-between gap-2 md:gap-4">
          {/* Logo */}
          <button 
            data-testid="logo-button"
            onClick={() => navigate('/home')}
            className="flex items-center gap-2 md:gap-3 hover:opacity-80 transition-opacity shrink-0"
          >
            <img 
              src="/uniconnect-logo.png" 
              alt="UniConnect" 
              className="w-8 h-8 md:w-10 md:h-10 rounded-xl object-cover"
            />
            <span className="font-bold text-lg md:text-xl text-gray-900 dark:text-white hidden sm:block" style={{ fontFamily: 'DM Sans' }}>UniConnect</span>
          </button>

          {/* Center Search */}
          <div className="flex-1 max-w-xl mx-2 md:mx-12 relative">
            <div className="relative">
              <Search className="absolute left-3 md:left-4 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500 z-10" />
              <Input
                type="text"
                placeholder="Search communities, messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.trim() && setShowSearchResults(true)}
                className="w-full pl-9 md:pl-11 pr-3 md:pr-4 h-9 md:h-10 bg-gray-50 dark:bg-[#1a1a1a] border-gray-200 dark:border-[#2a2a2a] rounded-xl text-sm dark:text-white dark:placeholder-gray-500 focus:border-indigo-300 dark:focus:border-purple-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-purple-900/30 transition-all"
              />
              <SearchDropdown
                isOpen={showSearchResults}
                searchQuery={searchQuery}
                results={searchResults}
                onClose={() => {
                  setShowSearchResults(false);
                  setSearchQuery('');
                }}
                navigate={navigate}
              />
            </div>
          </div>

          {/* Right Icons */}
          <div className="flex items-center gap-1 md:gap-3 shrink-0">
            <div className="relative">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative h-9 w-9 md:h-10 md:w-10 p-0 hover:bg-gray-100 dark:hover:bg-[#1a1a1a] rounded-xl"
              >
                <Bell className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                {notificationCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-purple-600 text-white text-xs font-bold rounded-full flex items-center justify-center">
                    {notificationCount > 9 ? '9+' : notificationCount}
                  </span>
                )}
              </Button>
              <NotificationDropdown 
                isOpen={showNotifications} 
                onClose={() => setShowNotifications(false)}
                notifications={notifications}
                onClearAll={handleClearAll}
                onClickNotification={handleClickNotification}
                navigate={navigate}
              />
            </div>
            <div className="h-6 w-px bg-gray-200 dark:bg-[#2a2a2a] hidden md:block"></div>
            {user ? (
              <button 
                onClick={() => navigate('/profile')}
                className="flex items-center gap-2 md:gap-2.5 hover:bg-gray-50 dark:hover:bg-[#1a1a1a] rounded-xl px-1.5 md:px-2.5 py-1.5 transition-colors"
              >
                <Avatar className="w-8 h-8 md:w-9 md:h-9 border-2 border-gray-100 dark:border-purple-500/30 shadow-sm">
                  <AvatarImage src={user?.picture && !user.picture.includes('dicebear') ? user.picture : undefined} />
                  <AvatarFallback 
                    className={isEmojiAvatar ? "bg-gray-100 dark:bg-[#1a1a1a] text-lg md:text-2xl" : "text-white text-xs md:text-sm font-bold"}
                    style={!isEmojiAvatar ? { background: 'linear-gradient(to bottom right, #a855f7, #7c3aed)' } : {}}
                  >
                    {isEmojiAvatar ? user?.profile_avatar : (user?.name?.charAt(0) || 'U')}
                  </AvatarFallback>
                </Avatar>
                <div className="text-left hidden md:block">
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">{user?.name || 'User'}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Student</div>
                </div>
              </button>
            ) : (
              <Button
                onClick={() => navigate('/login?mode=signup')}
                className="bg-purple-600 hover:bg-purple-700 text-white rounded-xl px-4 py-2"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </nav>

      <div className="flex flex-col md:flex-row gap-4">
        {/* Left Sidebar - Hidden on mobile */}
        <aside className="hidden md:block w-56 bg-white dark:bg-[#0f0f0f] dark:border dark:border-[#1a1a1a] rounded-2xl shadow-lg shadow-gray-200/50 dark:shadow-none p-4 self-start sticky top-24 shrink-0" data-testid="sidebar">
          <nav className="space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <button
                  key={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  onClick={() => navigate(item.path)}
                  className={`
                    w-full flex items-center gap-3 px-3.5 py-3 rounded-xl transition-all duration-200
                    ${
                      active
                        ? 'bg-indigo-50 dark:bg-purple-500/20 text-indigo-700 dark:text-purple-400 font-semibold'
                        : 'text-gray-700 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-[#1a1a1a] font-medium'
                    }
                  `}
                >
                  <Icon className="w-5 h-5" strokeWidth={2.5} />
                  <span className="text-sm">{item.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Main Content - Floating Container */}
        <main className="flex-1 bg-white dark:bg-[#0f0f0f] dark:border dark:border-[#1a1a1a] rounded-2xl shadow-lg shadow-gray-200/50 dark:shadow-none p-4 md:p-8 min-w-0">
          <Routes>
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="/home" element={<HomePage />} />
            <Route path="/communities" element={<CommunitiesPage unreadCommunities={unreadCommunities} />} />
            <Route path="/community/:groupId" element={<CommunityPage onMarkRead={(id) => {
              setUnreadCommunities(prev => {
                const newState = {...prev};
                delete newState[id];
                return newState;
              });
            }} />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/suggestions" element={<SuggestionsPage />} />
          </Routes>
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white dark:bg-[#0f0f0f] border-t border-gray-200 dark:border-[#1a1a1a] md:hidden z-50 safe-area-pb">
        <div className="flex justify-around py-2 px-1">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={`flex flex-col items-center gap-0.5 px-2 py-1.5 rounded-lg min-w-0 ${
                  active ? 'text-purple-500' : 'text-gray-500 dark:text-gray-400'
                }`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[10px]">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}